import { useId } from 'react';
import { Command, CommandInput, CommandList, CommandItem } from '@repo/ui/Command';
import { Mercury } from '@repo/types/Models';
import { Link, useNavigate } from 'react-router';

export interface Option {
  href: string;
  label: string;
}

export interface PaginatedComboboxProps {
  id?: string;
  pageSize: number;
  setQueryVars: (vars: Mercury.GQL.QueryVariables) => void;
  queryVars: Mercury.GQL.QueryVariables | null;
  loading?: boolean;
  options: Option[];
  pageInfo?: Mercury.GQL.PageInfo;
  op?: string;
}

export const PaginatedAccountCombobox = function PaginatedCombobox({
  id,
  pageSize,
  setQueryVars,
  queryVars,
  loading,
  options,
  pageInfo,
  op,
}: PaginatedComboboxProps) {
  const navigate = useNavigate();

  const handleQuery = (query: string) => {
    setQueryVars({
      first: pageSize,
      query: query,
      op: op,
    });
  };

  const handleLoadMore = () => {
    setQueryVars({
      first: pageSize,
      after: pageInfo?.endCursor,
      query: queryVars?.query,
      op: op,
    });
  };

  const handleLoadPrevious = () => {
    setQueryVars({
      last: pageSize,
      before: pageInfo?.startCursor,
      query: queryVars?.query,
      op: op,
    });
  };

  const defaultId = useId();
  id = id ?? defaultId;

  return (
    <div className="flex relative flex-col gap-1 py-1 w-full h-full border-t border-grey-400">
      <Command shouldFilter={false} className="bg-transparent">
        <CommandInput
          id={`${id}::combo`}
          autoComplete="off"
          placeholder="Find sub account"
          value={queryVars?.query ?? ''}
          onValueChange={handleQuery}
          className="placeholder:text-grey-500 dark:placeholder:text-grey-400 dark:border-grey-600 dark:focus:border-blue-300 bg-transparent"
        />

        <CommandList className="right-0 z-20 p-1 w-full focus:outline-none max-h-none overflow-visible">
          {loading
            ? Array.from({ length: pageSize }, (_, i) => (
                <div key={i} className="w-full">
                  <div className="block w-full h-[36px]">
                    <div className="flex items-center w-full h-full animate-pulse">
                      <div className="w-full h-8 rounded bg-grey-300 dark:bg-blue-900"></div>
                    </div>
                  </div>
                </div>
              ))
            : options?.map((option) => (
                <CommandItem
                  key={option.href}
                  value={option.label}
                  onSelect={() => {
                    navigate(option.href);
                  }}
                  className="rounded-md cursor-pointer hover:bg-blue-200 dark:hover:bg-blue-800 dark:text-blue-100 text-grey-1000 border-none data-[selected=true]:bg-blue-200 dark:data-[selected=true]:bg-blue-800"
                  asChild
                >
                  <Link to={option.href}>{option.label}</Link>
                </CommandItem>
              ))}

          {options?.length === 0 && !loading ? (
            <p className="py-2 px-2 text-sm">No accounts found matching search.</p>
          ) : null}
        </CommandList>
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={handleLoadPrevious}
            disabled={!pageInfo?.hasPreviousPage}
            className="w-full h-auto disabled:hidden"
          >
            <span className="text-2xl pointer-events-none dark:text-blue-100 text-grey-1000 material-symbols-outlined">
              arrow_circle_left
            </span>
          </button>

          <button
            type="button"
            onClick={handleLoadMore}
            disabled={!pageInfo?.hasNextPage}
            className="w-full h-auto disabled:hidden"
          >
            <span className="text-2xl pointer-events-none dark:text-blue-100 text-grey-1000 material-symbols-outlined">
              arrow_circle_right
            </span>
          </button>
        </div>
      </Command>
    </div>
  );
};
